function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}

function formatDateTime(value: Date): string {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())} ${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function nextWeekday(base: Date, weekday: number): Date {
  const next = new Date(base);
  const diff = (weekday + 7 - next.getDay()) % 7 || 7;
  next.setDate(next.getDate() + diff);
  return next;
}

function dateFromText(text: string): Date {
  const now = new Date();
  const normalized = normalize(text);
  const weekdays: Array<[RegExp, number]> = [
    [/\blunes\b/, 1],
    [/\bmartes\b/, 2],
    [/\bmiercoles\b/, 3],
    [/\bjueves\b/, 4],
    [/\bviernes\b/, 5],
    [/\bsabado\b/, 6],
    [/\bdomingo\b/, 0],
  ];
  const date = new Date(now);

  if (/\bpasado manana\b/.test(normalized)) date.setDate(date.getDate() + 2);
  else if (/\bmanana\b/.test(normalized)) date.setDate(date.getDate() + 1);
  else {
    const weekday = weekdays.find(([pattern]) => pattern.test(normalized));
    if (weekday) return nextWeekday(now, weekday[1]);
  }

  return date;
}

function timeFromText(text: string): { hours: number; minutes: number } {
  const normalized = normalize(text);
  const explicit = normalized.match(/\b(?:a las|desde las|para las)?\s*(\d{1,2})(?::| y )?(\d{2})?\b/);
  if (!explicit) return { hours: 9, minutes: 0 };

  let hours = Number(explicit[1]);
  const minutes = explicit[2] ? Number(explicit[2]) : 0;
  if (hours < 8 && /\btarde\b/.test(normalized)) hours += 12;
  return { hours: Math.min(Math.max(hours, 0), 23), minutes: Math.min(Math.max(minutes, 0), 59) };
}

function typeFromText(text: string): string {
  const normalized = normalize(text);
  if (/\b(llamar|llamado|telefono|whatsapp)\b/.test(normalized)) return 'Llamado';
  if (/\b(visita|visitar|domicilio|empresa)\b/.test(normalized)) return 'Visita';
  if (/\b(reunion|reunir|meet|equipo)\b/.test(normalized)) return 'Reunion';
  return 'General';
}

function titleFromText(text: string): string {
  const clean = cleanDictatedText(text);
  const first = clean.split(/[.;]/)[0] || clean;
  return first.length > 96 ? `${first.slice(0, 93)}...` : first;
}

function cleanDictatedText(text: string): string {
  let clean = text.trim().replace(/\s+/g, ' ');
  clean = clean.replace(/\bfuncionandoh\b/gi, 'funcionando');
  clean = clean.replace(/\btareds\b/gi, 'tareas');
  clean = clean.replace(/\binterpretador\b/gi, 'intérprete');
  clean = clean.replace(/\bcardiologico\b/gi, 'cardiológico');
  clean = clean.replace(/\bclinico\b/gi, 'clínico');
  clean = clean.replace(/\bademas\b/gi, 'además');
  clean = clean.replace(/\bpor que\b/gi, 'por qué');
  clean = clean.replace(/\b(.{18,120}?)\s+de\s+\1\b/gi, '$1');
  return clean.replace(/(^|[.!?]\s+)([a-záéíóúñ])/g, (_, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
}

function setField(root: HTMLElement, name: string, value: string): void {
  const field = root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${name}"]`);
  if (!field) return;
  field.value = value;
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

function applySmartTask(root: HTMLElement): void {
  const source = root.querySelector<HTMLTextAreaElement>('[data-profint-smart-task-source]');
  const status = root.querySelector<HTMLElement>('[data-profint-smart-task-status]');
  const text = source?.value.trim() ?? '';
  if (!text) {
    if (status) status.textContent = 'Dictá o escribí una instrucción para interpretar.';
    return;
  }

  const start = dateFromText(text);
  const time = timeFromText(text);
  start.setHours(time.hours, time.minutes, 0, 0);
  const end = new Date(start);
  end.setHours(end.getHours() + 1);

  const cleanText = cleanDictatedText(text);
  setField(root, 'Titulo', titleFromText(cleanText));
  setField(root, 'Descripcion', cleanText);
  setField(root, 'FechaInicio', formatDateTime(start));
  setField(root, 'FechaFin', formatDateTime(end));
  setField(root, 'Tipo', typeFromText(text));
  setField(root, 'Estado', 'Pendiente');
  if (status) status.textContent = 'Borrador generado con reglas locales. Revisá responsable, fecha y horario antes de guardar.';
}

function enhance(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>('[data-profint-smart-task]').forEach((root) => {
    if (root.dataset.profintSmartTaskMounted) return;
    root.dataset.profintSmartTaskMounted = '1';
    root.querySelector<HTMLButtonElement>('[data-profint-smart-task-apply]')?.addEventListener('click', () => applySmartTask(root));
  });
}

document.addEventListener('DOMContentLoaded', () => enhance());
document.body?.addEventListener('htmx:afterSwap', (event) => {
  const target = (event as CustomEvent).detail?.target as ParentNode | undefined;
  enhance(target ?? document);
});
